import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Plus, Loader2, Pencil, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Manage the current user's SMS quick-reply templates.
 * Backed by GET/POST/PUT/DELETE /api/sms/templates[...].
 * Also lets the user set their booking link so the ``{{booking_link}}``
 * placeholder in any template auto-fills, and pick optional
 * lead-status / lead-category tags so a template gets surfaced in the
 * "Suggested" row on matching leads.
 */
const csvFromList = (arr) => (arr || []).join(', ');
const listFromCsv = (str) => (str || '')
  .split(',')
  .map(s => s.trim())
  .filter(Boolean);

function TemplateRow({ tpl, onDelete, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(tpl.label);
  const [body, setBody] = useState(tpl.body);
  const [statuses, setStatuses] = useState(csvFromList(tpl.match_statuses));
  const [categories, setCategories] = useState(csvFromList(tpl.match_categories));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!label.trim() || !body.trim()) { toast.error('Label and body are required'); return; }
    setSaving(true);
    try {
      await api.updateSmsTemplate(tpl.id, {
        label: label.trim(),
        body: body.trim(),
        match_statuses: listFromCsv(statuses),
        match_categories: listFromCsv(categories),
      });
      toast.success('Template updated');
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const cancel = () => {
    setLabel(tpl.label);
    setBody(tpl.body);
    setStatuses(csvFromList(tpl.match_statuses));
    setCategories(csvFromList(tpl.match_categories));
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="rounded-sm border border-[#0047AB] p-2 space-y-1.5" data-testid={`sms-template-edit-${tpl.id}`}>
        <Input value={label} onChange={e => setLabel(e.target.value)} maxLength={40} className="text-sm rounded-sm" placeholder="Label" />
        <Textarea value={body} onChange={e => setBody(e.target.value)} rows={3} maxLength={1200} className="text-sm rounded-sm" placeholder="Message" />
        <Input value={statuses} onChange={e => setStatuses(e.target.value)} className="text-xs rounded-sm" placeholder="Auto-suggest for statuses (comma-separated, e.g. New, Contacted)" data-testid={`sms-template-edit-statuses-${tpl.id}`} />
        <Input value={categories} onChange={e => setCategories(e.target.value)} className="text-xs rounded-sm" placeholder="Auto-suggest for categories (comma-separated, e.g. Healthcare, Real Estate)" data-testid={`sms-template-edit-categories-${tpl.id}`} />
        <div className="flex gap-1.5 justify-end">
          <Button variant="ghost" size="sm" onClick={cancel} className="h-7 rounded-sm text-xs" data-testid={`sms-template-cancel-${tpl.id}`}>
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="h-7 rounded-sm text-xs bg-slate-900 hover:bg-slate-800 text-white" data-testid={`sms-template-save-${tpl.id}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />} Save
          </Button>
        </div>
      </div>
    );
  }

  const tags = [
    ...(tpl.match_statuses || []).map(s => ({ k: `s-${s}`, label: s, tone: 'text-[#0047AB] bg-[#EFF6FF]' })),
    ...(tpl.match_categories || []).map(c => ({ k: `c-${c}`, label: c, tone: 'text-emerald-700 bg-emerald-50' })),
  ];

  return (
    <div className="flex items-start gap-2 rounded-sm border border-slate-200 dark:border-slate-700 p-2" data-testid={`sms-template-row-${tpl.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{tpl.label}</p>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 line-clamp-2">{tpl.body}</p>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1" data-testid={`sms-template-tags-${tpl.id}`}>
            {tags.map(t => (
              <span key={t.k} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${t.tone}`}>{t.label}</span>
            ))}
          </div>
        )}
      </div>
      <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-8 w-8 p-0 text-slate-500 hover:bg-slate-100" data-testid={`sms-template-edit-btn-${tpl.id}`}>
        <Pencil className="w-3.5 h-3.5" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onDelete(tpl.id)} className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" data-testid={`sms-template-delete-${tpl.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

export function SmsTemplatesDialog({ open, onOpenChange, onChanged }) {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState('');
  const [body, setBody] = useState('');
  const [statuses, setStatuses] = useState('');
  const [categories, setCategories] = useState('');
  const [saving, setSaving] = useState(false);
  const [bookingLink, setBookingLink] = useState(user?.booking_link || '');
  const [savingLink, setSavingLink] = useState(false);

  useEffect(() => { setBookingLink(user?.booking_link || ''); }, [user?.booking_link]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.getSmsTemplates();
      setItems(data.templates || []);
      onChanged && onChanged(data.templates || []);
    } catch { toast.error('Could not load templates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  const add = async () => {
    if (!label.trim() || !body.trim()) { toast.error('Label and body are required'); return; }
    setSaving(true);
    try {
      await api.createSmsTemplate({
        label: label.trim(),
        body: body.trim(),
        match_statuses: listFromCsv(statuses),
        match_categories: listFromCsv(categories),
      });
      setLabel(''); setBody(''); setStatuses(''); setCategories('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.deleteSmsTemplate(id); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const saveBookingLink = async () => {
    setSavingLink(true);
    try {
      await api.updateBookingLink(bookingLink.trim());
      toast.success('Booking link saved');
      refreshUser && refreshUser();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSavingLink(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sms-templates-dialog">
        <DialogHeader>
          <DialogTitle>Quick Reply Templates</DialogTitle>
          <DialogDescription>
            Save canned replies you send often. Tap one on any lead to fill the compose box.
            Tokens:{' '}
            <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded-sm">{'{{first_name}}'}</code>,{' '}
            <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded-sm">{'{{booking_link}}'}</code>,{' '}
            <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded-sm">{'{{company}}'}</code>,{' '}
            <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded-sm">{'{{status}}'}</code>,{' '}
            <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded-sm">{'{{owner_name}}'}</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-sm border border-slate-200 dark:border-slate-700 p-2.5 space-y-1.5">
          <label className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Your booking link</label>
          <div className="flex gap-2">
            <Input
              value={bookingLink}
              onChange={e => setBookingLink(e.target.value)}
              placeholder="https://cal.com/you/intro"
              maxLength={500}
              className="text-sm rounded-sm flex-1"
              data-testid="sms-booking-link-input"
            />
            <Button
              onClick={saveBookingLink}
              disabled={savingLink || bookingLink === (user?.booking_link || '')}
              size="sm"
              className="rounded-sm text-sm bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="sms-booking-link-save"
            >
              {savingLink ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto -mx-2 px-2">
            {items.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-4 text-center">No templates yet.</p>
            ) : items.map(tpl => (
              <TemplateRow key={tpl.id} tpl={tpl} onDelete={remove} onSaved={load} />
            ))}
          </div>
        )}

        <div className="border-t border-slate-200 dark:border-slate-700 pt-3 space-y-2">
          <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Add new</p>
          <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (e.g. Interested)" className="text-sm rounded-sm" maxLength={40} data-testid="sms-template-new-label" />
          <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Message body" rows={3} className="text-sm rounded-sm" maxLength={1200} data-testid="sms-template-new-body" />
          <Input value={statuses} onChange={e => setStatuses(e.target.value)} placeholder="Auto-suggest for statuses (comma-separated) — optional" className="text-xs rounded-sm" data-testid="sms-template-new-statuses" />
          <Input value={categories} onChange={e => setCategories(e.target.value)} placeholder="Auto-suggest for categories (comma-separated) — optional" className="text-xs rounded-sm" data-testid="sms-template-new-categories" />
          <Button onClick={add} disabled={saving || !label.trim() || !body.trim()} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-sm" data-testid="sms-template-add-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
            Add template
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
